import React, {Component} from 'react';
import {moment} from "../lib/ext-refs";
import {
    getCardPresentPmDescriptionByCode,
    getPmDescriptionByCode,
    getPtDescriptionByCode,
    maskNumber,
    sendGoogleAnalyticsEvent
} from "../lib/utils";
import {
    cashRequiresCheckNumber,
    formatAmount,
    isDirectDebitCat,
    isCardPresentCat,
    pmTypeRequiresAccountNumber,
    isCash
} from "../lib/payment-utils";
import ReactToPrint from 'react-to-print';

/**
 * Shows the receipt of the processed payment.
 * Expected properties:
 * - config
 * - data
 */
class Receipt extends Component {
    constructor(props) {
        super(props);
        this.makeNewPayment = this.makeNewPayment.bind(this);
    }

    componentDidUpdate() {
        const {data, config, setValue} = this.props,
            {paymentResponse, gaReceiptEventSent} = data,
            {messages} = config,
            {paymentStatus, paymentAmount} = paymentResponse;
        //ensure that google analytics event is only sent a single time, this variable is cleared when the user selects to make another payment
        //ensuring that the event is sent when the second payment is completed
        if(!gaReceiptEventSent) {
            // scroll to top when receipt page is rendered; otherwise, with long receipts the user will be at the bottom of the page
            window.scrollTo(0, 0);
            setValue('gaReceiptEventSent', true);
            // ga only supports integer in eventValue
            if (paymentStatus === 'ACCEPTED' || paymentStatus === 'SCHEDULED') {
                sendGoogleAnalyticsEvent('Payment', messages["ga.event.action.paymentProcessed." + paymentStatus] || 'Payment Processed Successfully', this.props.config.gaEventLabel, parseInt(paymentAmount, 10));
            } else {
                sendGoogleAnalyticsEvent('Payment', messages["ga.event.action.paymentProcessed." + paymentStatus] || 'Payment Processed Unsuccessfully', this.props.config.gaEventLabel, parseInt(paymentAmount, 10));
            }
        }
    }

    makeNewPayment() {
        const {handleMakeNewPayment} = this.props;
        handleMakeNewPayment();
    }

    render() {
        const {config, data, showIcon, hideCnvFee, handleMapPaymentsForDisplay, showAgentName, additionalDataUnderLogo, customLogo} = this.props,
            {payments, paymentResponse, paymentMethod, customer} = data,
            {paymentConfig, messages} = config,
            {paymentMethodCategory, type} = paymentMethod,
            {paymentStatus} = paymentResponse,
            hasBundle = false;
        const paymentScheduled = (paymentStatus === 'SCHEDULED');
        const paymentAccepted = (paymentStatus === 'ACCEPTED');
		if (data.ivrStatus) {
		      paymentResponse.paymentAmount = data.ivrStatus.paymentAmount;
		      paymentResponse.convenienceFee = data.ivrStatus.convenienceFee;
		      paymentResponse.totalAmount =
		        parseFloat(paymentResponse.paymentAmount) +
		        parseFloat(data.ivrStatus.convenienceFee);
		    }

        let pmAccountNumberLabel = messages["payment.cardNumber.label"];
        let acctNumber = [];
        if (isDirectDebitCat(type)) {
            // If payment method is direct debit, in payment response it will be
            // set as masked acct number - masked routing number
            acctNumber = paymentMethod.accountNumber.split("-");
            if (acctNumber.length <= 1 || acctNumber[1] === '') {
                acctNumber = paymentMethod.accountNumber;
            } else {
                acctNumber = acctNumber[1];
            }
            pmAccountNumberLabel = messages["payment.bankAccountNumber.label"];
        } else {
            acctNumber = paymentMethod.accountNumber;
        }

        let cashChange = 0.0;
        let cashTenderedAmount = paymentMethod.cashTendered;
        let paymentAmount = data.totalPaymentAmount;
        if (cashTenderedAmount && cashTenderedAmount > 0 && cashTenderedAmount > paymentAmount) {
            cashChange = cashTenderedAmount - paymentAmount;
        }

        return (
            <div className={"opp-section"} id="Receipt" ref={el => (this.componentRef = el)}>
                <h3 className="section-title">
                    {showIcon && (
                        <span className="section-icon">
                            <img src={messages["opp.icon-receipt"] || "images/icon-receipt.png"} alt="Payment Receipt" />
                        </span>)
                    }
                    <span className="section-text">{messages["opp.paymentReceipt.title"]}</span>
                </h3>
                <div className="row">
                    <div className="col-12">
                        <div className="card card-v2">
                            <div className="card-body">
                                {paymentStatus === "FAILED" && (
                                    <React.Fragment>
                                        <p><strong>{messages["payment.failed.label"]}</strong></p>
                                        <LogoItem customLogo={customLogo} />
                                    </React.Fragment>
                                    
                                )}
                                {paymentStatus === "TIMEOUT" && (
                                    <React.Fragment>
                                        <p><strong>{messages["payment.timedout.label"]}</strong></p>
                                        <LogoItem customLogo={customLogo} />
                                    </React.Fragment>
                                )}
                                {paymentStatus === "VOID" && isCardPresentCat(paymentMethodCategory) && (
                                    <React.Fragment>
                                        <p><strong>{messages["card.present.payment.voided.label"]}</strong></p>
                                        <LogoItem customLogo={customLogo} />
                                    </React.Fragment>
                                )}
                                {paymentStatus === "DECLINED" && (
                                    <React.Fragment key={"Declined"}>
                                        <p><strong>{messages["payment.declined.label"]}</strong></p>
                                        {paymentConfig.declinedPaymentStatusEnabled && paymentResponse.paymentStatusDescription && (
                                            <p> {messages["otp.payment.status.description.label"]}
                                                : {paymentResponse.paymentStatusDescription} </p>
                                        )}
                                        <LogoItem customLogo={customLogo} />
                                    </React.Fragment>
                                )}
                                {(paymentAccepted || paymentScheduled) && (
                                    <React.Fragment key={"Accepted"}>
                                        <p key="Label1">{messages["otp.titleSubmittedComment.label"]}</p>
                                        <p key="Label2">{messages[paymentScheduled ? "otp.paymentScheduled.text" : "otp.paymentAccepted.text"]}</p>
                                        <LogoItem customLogo={customLogo} />
                                        {additionalDataUnderLogo ? additionalDataUnderLogo() : ''}
                                        <h3 className="payment-component-title mb-0">{messages["opp.payment.details.title"]}</h3>
                                        <table
                                            className="table table-receipt table-hover table-sm-custom table-bordered-simple table-vertical-middle">
                                            <tbody>
                                            <ReceiptItemRow
                                                key="refNr"
                                                label={messages["payment.referenceNumber.label"]}
                                                value={paymentResponse.referenceNumber}
                                            />
                                            <ReceiptItemRow
                                                key="paymentDate"
                                                label={messages["payment.date.label"]}
                                                value={moment(paymentResponse.paymentDate, 'MMDDYYYYHHmmss').format(
                                                    paymentScheduled
                                                        ? "MMMM D, YYYY"
                                                        : "MMMM D, YYYY h:mm:ss a"
                                                )}
                                            />
                                            {hasBundle &&
                                            <tr>
                                                <td className="payment-type-col" colSpan={2}>
                                                    <table
                                                        className="table table-component table-sm-custom table-vertical-middle mt-3">
                                                        <tbody>
                                                        <tr>
                                                            <td>{messages["payment.components.label"]}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>shopping cart details</td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                            }
                                            {/* FIXME: Better display of this */}
                                            {/* If handleMapPaymentsForDisplay function is passed, use that to render instead of what is below */}
                                            {handleMapPaymentsForDisplay ? handleMapPaymentsForDisplay() : payments.map((payment, idx) => (
                                                <React.Fragment key={`Payment-${idx}`}>
                                                    <ReceiptItemRow
                                                        key={`Payment-${idx}`}
                                                        label={messages["payment.account.label"]}
                                                        value={payment.header.accountNumber}
                                                    />
                                                    <ReceiptItemRow
                                                        key={`CustomerName-${idx}`}
                                                        label={messages["payment.customerName.label"]}
                                                        value={
                                                            `${customer.firstName} ${customer.middleName || ''} ${customer.lastName}`
                                                        }
                                                    />
                                                    <ReceiptItemRow
                                                        key={`PaymentType-${idx}`}
                                                        label={messages["payment.type.label"]}
                                                        value={getPtDescriptionByCode(
                                                            paymentConfig,
                                                            payment.header.paymentTypeCode
                                                        )}
                                                    />
                                                    <ShoppingCartRecipt
                                                        key={`ShoppingCartRecipt-${idx}`}
                                                        idx={idx}
                                                        payment={payment}
                                                    />
                                                </React.Fragment>
                                            ))}

                                            <ReceiptItemRow
                                                key="PaymentMethod"
                                                label={messages["payment.method.label"]}
                                                value={isCardPresentCat(paymentMethodCategory) ? getCardPresentPmDescriptionByCode(type) : getPmDescriptionByCode(paymentConfig, type)}
                                            />
                                            {/* Do not show Routing number to match current payment flow */}
                                            {isCash(type) && (
                                                <ReceiptItemRow
                                                    key="PaymentMethodCashTendered"
                                                    label={messages["payment.cashTendered.label"] || "Cash Tendered"}
                                                    value={formatAmount(paymentMethod.cashTendered)}
                                                />)
                                            }
                                            {isCash(type) && (
                                                <ReceiptItemRow
                                                    key="PaymentMethodCashChange"
                                                    label={messages["payment.cashChange.label"] || "Cash Change"}
                                                    value={formatAmount(cashChange)}
                                                />)
                                            }
                                            {pmTypeRequiresAccountNumber(type) && (
                                                <ReceiptItemRow
                                                    key="PaymentMethodAccount"
                                                    label={pmAccountNumberLabel}
                                                    value={maskNumber(acctNumber)}
                                                />)
                                            }
                                            {cashRequiresCheckNumber(type) && (
                                                <ReceiptItemRow
                                                    key="PaymentMethodCheck"
                                                    label={messages["payment.checkNumber.label"]}
                                                    value={paymentMethod.checkNumber}
                                                />
                                            )}
                                            
                                            {!hideCnvFee &&
                                            <React.Fragment key={`PaymentAmountAndFee`}>
                                                <ReceiptItemRow
                                                    key="PaymentAmount"
                                                    label={messages["payment.amount.label"]}
                                                    value={formatAmount(paymentResponse.paymentAmount)}
                                                />
                                                <ReceiptItemRow
                                                    key="PaymentFee"
                                                    label={messages["payment.amount.fee.label"]}
                                                    value={formatAmount(paymentResponse.convenienceFee)}
                                                />
                                            </React.Fragment>
                                            }
                                            <ReceiptItemRow
                                                key="PaymentTotal"
                                                trClass="total-line"
                                                label={messages["payment.amount.total.label"]}
                                                value={formatAmount(paymentResponse.totalAmount)}
                                            />
                                            {showAgentName &&
                                            <ReceiptItemRow
                                                key="AgentName"
                                                label={messages["opp.agentName.label"]}
                                                value={config.agentFirstName + " " + config.agentLastName.substring(0, 1)}
                                            />
                                            }

                                            {isCardPresentCat(paymentMethodCategory) && (
                                                <React.Fragment>
                                                    <ReceiptItemRow/>
                                                    <ReceiptItemRow
                                                        key="CardEntryMode"
                                                        label={messages["opp.paymentMethod.card.entry.mode.label"]}
                                                        value={paymentMethod.cardEntryMode}
                                                    />
                                                    <ReceiptItemRow
                                                        key="AuthorizationMode"
                                                        label="Authorization Mode"
                                                        value={paymentMethod.authMode}
                                                    />
                                                    <ReceiptItemRow
                                                        key="AID"
                                                        label={messages["opp.paymentMethod.aid.label"]}
                                                        value={paymentMethod.aid}
                                                    />
                                                    <ReceiptItemRow
                                                        key="TVR"
                                                        label={messages["opp.paymentMethod.tvr.label"]}
                                                        value={paymentMethod.tvr}
                                                    />
                                                    <ReceiptItemRow
                                                        key="TSI"
                                                        label={messages["opp.paymentMethod.tsi.label"]}
                                                        value={paymentMethod.tsi}
                                                    />
                                                    <ReceiptItemRow
                                                        key="ARC"
                                                        label="ARC"
                                                        value={paymentMethod.arc}
                                                    />
                                                    <ReceiptItemRow
                                                        key="IAD"
                                                        label="IAD"
                                                        value={paymentMethod.iad}
                                                    />
                                                </React.Fragment>
                                            )}
                                            </tbody>
                                        </table>
                                    </React.Fragment>)
                                }
                                <div className="card-footer flex-footer mt-3 pl-0 pb-0">
                                    <ReactToPrint
                                        trigger={() =>
                                            <button
                                                type="button"
                                                className="btn btn-primary print-receipt ml-1 mr-1"
                                            >
                                                {messages["otp.printConfirmation.link"]}
                                            </button>
                                        }
                                        content={() => this.componentRef}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-primary make-new-payment ml-1 mr-1"
                                        onClick={this.makeNewPayment}
                                    >
                                        {messages["opp.make.new.payment.label"]}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
class ShoppingCartRecipt extends Component {
  render() {
    const { payment, idx } = this.props;
    let bundle = undefined;
    let hasBundle = false;
    if (payment.bundle) {
      bundle = payment.bundle;
    }
    const shoppingCartItemJsx = [];
    if (bundle && bundle.paymentItems) {
      hasBundle = true;
      if (bundle.paymentItems.length > 0) {
        bundle.paymentItems.forEach((item, index) => {
          shoppingCartItemJsx.push(
            <ReceiptItemRow
              key={idx + item.code}
              label={item.label}
              value={formatAmount(item.paymentAmount)}
            />
          );
        });
      } else {
        shoppingCartItemJsx.push(
          <ReceiptItemRow
            key={idx + bundle.paymentItems.code}
            label={bundle.paymentItems.label}
            value={formatAmount(bundle.paymentItems.paymentAmount)}
          />
        );
      }
    }

    return (
      <React.Fragment key={"paymentBundleEntries" + idx}>
        {hasBundle && (
          <tr>
            <td colSpan="2">{messages["payment.components.label"]}</td>
          </tr>
        )}
        {hasBundle && (
          <tr>
            <td className="payment-type-col" colSpan={2}>
              <table className="table table-component table-sm-custom table-vertical-middle mt-2">
                <tbody>{shoppingCartItemJsx}</tbody>
              </table>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  }
}

const ReceiptItemRow = ({label, value, trClass, tdColClass, tdCol2Class}) => (
    <tr className={trClass}>
        <td className={tdColClass || 'payment-type-col'}>{label}</td>
        <td className={tdCol2Class || 'payment-amount-col text-right'}>{value}</td>
    </tr>
);

/**
 * Show the biller's logo on the receipt page if the action message 'opp.logo-receipt' is defined
 */
const LogoItem = ({customLogo}) => {
    const logoMsgKey = customLogo || messages["opp.logo-receipt"];
    if (!logoMsgKey) {
        return null; // render nothing
    }
    
    return (
        <span className="section-icon">
           <img src={logoMsgKey} alt="Payment Receipt"/>
        </span>
    );
};

export { Receipt, ReceiptItemRow, ShoppingCartRecipt, LogoItem };
